import { useState } from 'react';
import { themeClasses, Button } from '@member-portal/ui';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';

interface PartnerInquiryFormProps {
  source: string;
  sectionId?: string;
  className?: string;
}

function PartnerInquiryForm({ source, sectionId = 'partner-inquiry', className = '' }: PartnerInquiryFormProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: 'US',
    phone: '',
    supplierName: '',
    supplierWebsite: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const countries = getCountries().sort((a, b) => {
    if (a === 'US') return -1;
    if (b === 'US') return 1;
    return (en[a] || a).localeCompare(en[b] || b);
  });

  const formatPhoneDisplay = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const getFormattedPhone = () => {
    const digits = formData.phone.replace(/\D/g, '');
    if (digits.length === 0) return '';
    const callingCode = getCountryCallingCode(formData.countryCode as any);
    return `+${callingCode} (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      setFormData({ ...formData, phone: formatPhoneDisplay(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    if (error) setError(null);
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      const response = await fetch(`/api/fhxd-auth/register-partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: getFormattedPhone(),
          supplierName: formData.supplierName,
          supplierWebsite: formData.supplierWebsite,
          description: formData.description,
          source,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
      } else if (data.field) {
        setFieldErrors({ [data.field]: data.message });
      } else {
        setError(data.message || 'Submission failed. Please try again.');
      }
    } catch (err) {
      console.error('Partner inquiry error:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id={sectionId}
      className={`py-16 bg-theme-bg scroll-mt-[var(--fhxd-chrome-height)] ${className}`}
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className={`${themeClasses.title.h3} text-center mb-12`}>Partnership Inquiry</h2>

        {success ? (
          <div className="text-center py-8">
            <h3 className={`${themeClasses.title.h4} mb-4`}>Inquiry Received</h3>
            <p className={themeClasses.text.body}>
              Thank you for your interest in partnering with Four Hundred by Design. Our team
              will review your inquiry and be in touch soon.
            </p>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className={themeClasses.form.label}>
                    First Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    className={`${themeClasses.form.input} font-normal`}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className={themeClasses.form.label}>
                    Last Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    className={`${themeClasses.form.input} font-normal`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className={themeClasses.form.label}>
                    Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    className={`${themeClasses.form.input} font-normal ${
                      fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  />
                  {fieldErrors.email && (
                    <p className="mt-2 text-sm font-body font-medium text-red-500">{fieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="phone" className={themeClasses.form.label}>
                    Phone Number <span className="text-red-600">*</span>
                  </label>
                  <div className="flex">
                    <select
                      id="countryCode"
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className={`${themeClasses.form.select} font-normal !w-20 flex-shrink-0 border-r-0 px-2`}
                    >
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          +{getCountryCallingCode(country)}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                      maxLength={14}
                      className={`${themeClasses.form.input} font-normal flex-1`}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="supplierName" className={themeClasses.form.label}>
                  Supplier Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="supplierName"
                  name="supplierName"
                  required
                  value={formData.supplierName}
                  onChange={handleChange}
                  placeholder="Hotel, Brand, or Company Name"
                  className={`${themeClasses.form.input} font-normal`}
                />
              </div>

              <div>
                <label htmlFor="supplierWebsite" className={themeClasses.form.label}>
                  Supplier Website <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="supplierWebsite"
                  name="supplierWebsite"
                  required
                  value={formData.supplierWebsite}
                  onChange={handleChange}
                  placeholder="www.example.com"
                  className={`${themeClasses.form.input} font-normal ${
                    fieldErrors.supplierWebsite
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : ''
                  }`}
                />
                {fieldErrors.supplierWebsite && (
                  <p className="mt-2 text-sm font-body font-medium text-red-500">
                    {fieldErrors.supplierWebsite}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="description" className={themeClasses.form.label}>
                  Description <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={5}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Tell us about your brand, business, or offering."
                  className={`${themeClasses.form.input} font-normal resize-y min-h-[140px]`}
                />
              </div>

              <div className="text-center pt-6">
                <Button type="submit" variant="primary" disabled={loading} className="min-w-[200px]">
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

export default PartnerInquiryForm;
